import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { WOStatusBadge, WO_STATUS_TONE } from "../components/etams/StatusBadge";
import { Factory, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { jamPesanan, useStoreTimezones } from "../lib/timezone";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

// Peta status pindah ke `WO_STATUS_TONE` di components/etams/StatusBadge
// (KONTRAS-01 Batch 11). Peta di sini dan yang kembar di WODetail melayani
// SATU kolom yang sama, jadi keduanya dilebur — bukan disalin dua kali.

// Work Order — pola daftar seragam mode SERVER (28 Sep 2026; `docs/POLA_DAFTAR.md`).
// Cari nomor WO, urut, saringan status, paginasi, unduhan di server; posisi di alamat.
// Saringan bawaan "Belum selesai" (WO selesai/dibatalkan tidak ikut) — chip, bisa dihapus.

//: Cermin `URUT_SAH_WO` di backend/routers/wo.py.
const URUT_SAH = ["created_at", "wo_number", "status", "locked_at"];
const BELUM_SELESAI = "COMPLETED,CANCELLED";

const KOLOM_CSV = [
  { kunci: "wo_number", judul: "Nomor WO" },
  { kunci: "status", judul: "Status", ambil: (r) => WO_STATUS_TONE[r.status]?.l || r.status },
  { kunci: "store_name", judul: "Cabang", ambil: (r) => r.store_name || r.store?.name || "" },
  { kunci: "fa", judul: "Jumlah FA", ambil: (r) => (r.source_fa_ids || []).length },
  { kunci: "total_qty_atasan", judul: "Qty atasan", ambil: (r) => r.total_qty_atasan || 0 },
  { kunci: "total_qty_celana", judul: "Qty celana", ambil: (r) => r.total_qty_celana || 0 },
  { kunci: "locked_at", judul: "Dikunci", ambil: (r) => jamPesanan(r.locked_at, r.store_id || r.store?.id, r.region_id || r.region?.id) },
  { kunci: "created_at", judul: "Dibuat", ambil: (r) => jamPesanan(r.created_at, r.store_id || r.store?.id, r.region_id || r.region?.id) },
];


export default function WOList() {
  const { isOwner, isKepalaCabang, profile } = useAuth();
  useStoreTimezones();   // T93: jam menurut zona cabang WO + label WIB/WITA
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const canCreate = isOwner || isKepalaCabang || ["ADMIN_PRODUKSI", "CS"].includes(profile?.role?.code);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      aktif: { label: "Tampil", param: "status_kecuali", bawaan: BELUM_SELESAI, tampil: () => "Belum selesai" },
      status: { label: "Status", param: "status", tampil: (v) => WO_STATUS_TONE[v]?.l || v },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan,
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/wo", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/wo", { params: bangunParams() });
      setItems(r.data?.items || []);
      setTotal(typeof r.data?.total === "number" ? r.data.total : 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat daftar WO."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);
  // Auto-poll 10s (polling fallback pattern)
  useEffect(() => {
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const adaSaringan = daftar.cari.trim() || daftar.saringanAktif.length;

  return (
    <div className="space-y-6" data-testid="wo-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Factory className="h-6 w-6" /> Work Order / Produksi
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Kelola WO dari FA yang layak (regular / FOC / Special Customer / Piutang).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="wo-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
          </Button>
          {canCreate && (
            <Button size="sm" onClick={() => navigate("/wo/new")} data-testid="wo-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Buat WO
            </Button>
          )}
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor WO" testId="wo-palang"
                    ekspor={{ nama: "work_order", kolom: KOLOM_CSV, ambilHalaman }}>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={daftar.nilaiSaringan.aktif}
          onChange={(e) => daftar.setSaringan("aktif", e.target.value)}
          aria-label="Saring WO selesai"
          data-testid="wo-filter-aktif"
        >
          <option value={BELUM_SELESAI}>Belum selesai</option>
          <option value="">Semua WO</option>
        </select>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={daftar.nilaiSaringan.status}
          onChange={(e) => (e.target.value ? daftar.setBeberapaSaringan({ status: e.target.value, aktif: "" }) : daftar.setSaringan("status", ""))}
          aria-label="Saring status"
          data-testid="wo-filter-status"
        >
          <option value="">Semua status</option>
          {Object.keys(WO_STATUS_TONE).map((k) => (
            <option key={k} value={k}>{WO_STATUS_TONE[k].l}</option>
          ))}
        </select>
      </PalangDaftar>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="wo_number" className="px-4 py-3 font-medium">Nomor WO</ThUrut>
                <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                <th className="px-4 py-3 font-medium">Cabang</th>
                <th className="px-4 py-3 font-medium">FA sumber</th>
                <th className="px-4 py-3 font-medium">Qty (Atasan/Celana)</th>
                <ThUrut daftar={daftar} kunci="locked_at" className="px-4 py-3 font-medium">Dikunci</ThUrut>
                <ThUrut daftar={daftar} kunci="created_at" className="px-4 py-3 font-medium">Dibuat</ThUrut>
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Memuat…</td></tr>
              ) : daftar.tampil.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground" data-testid="wo-empty">
                  {adaSaringan
                    ? "Tidak ada Work Order yang cocok dengan pencarian atau saringan ini."
                    : <>Belum ada Work Order. {canCreate && <Link to="/wo/new" className="text-primary underline">+ Buat WO baru</Link>}</>}
                </td></tr>
              ) : daftar.tampil.map((r) => {
                return (
                  <tr key={r.id} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link to={`/wo/${r.id}`} className="text-primary hover:underline" data-testid={`wo-row-${r.wo_number}`}>{r.wo_number}</Link>
                    </td>
                    <td className="px-4 py-3"><WOStatusBadge value={r.status} /></td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <div className="text-xs">{r.store_name || r.store?.name || "—"}</div>
                    </td>
                    <td className="px-4 py-3">{(r.source_fa_ids || []).length} FA</td>
                    <td className="px-4 py-3">{r.total_qty_atasan || 0} / {r.total_qty_celana || 0}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{jamPesanan(r.locked_at, r.store_id || r.store?.id, r.region_id || r.region?.id)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{jamPesanan(r.created_at, r.store_id || r.store?.id, r.region_id || r.region?.id)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="wo-paginasi" />
      </Card>
    </div>
  );
}
